/**
 * TemplateMethodologyControl — a qué metodología pertenece la plantilla,
 * en la cabecera de todos los editores (OT-0055).
 *
 * La pertenencia es de la plantilla, no de la versión: se cambia aunque la
 * versión vigente esté publicada, con `PUT|DELETE /methodologies/{id}/
 * templates/{template_id}`. Solo el admin, solo plantillas del tenant no
 * archivadas; en el resto de casos se muestra la etiqueta.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  assignTemplateToMethodology,
  unassignTemplateFromMethodology,
} from '../../../api/methodologies'
import type { Template } from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useMe } from '../../../auth/useMe'
import { useApiErrorMessage } from '../../../hooks/useApiErrorMessage'
import { METHODOLOGIES_QUERY_KEY, useMethodologies } from '../../../hooks/useMethodologies'
import { templateMethodologyLabel } from '../../../lib/methodologyLabel'
import { canManageTenant } from '../../../lib/tiers'
import { pushToast } from '../../../stores/toastStore'
import { MethodologyPicker } from '../methodologies/MethodologyPicker'

export function TemplateMethodologyControl({ template }: { template: Template }) {
  const { t } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  const queryClient = useQueryClient()
  const apiErrorMessage = useApiErrorMessage()
  const { byId } = useMethodologies()

  const editable =
    canManageTenant(me?.role, me?.tenant?.access) &&
    template.tenant_id !== null &&
    template.status !== 'archived'

  const mutation = useMutation({
    mutationFn: (next: string | null) => {
      if (!accessToken) throw new Error('no_access_token')
      if (next !== null) return assignTemplateToMethodology(accessToken, next, template.id)
      if (template.methodology_id === null) throw new Error('already_generic')
      return unassignTemplateFromMethodology(accessToken, template.methodology_id, template.id)
    },
    onSuccess: (ref) => {
      // Una metodología recién creada ahí mismo aún no está en la lista.
      const name = ref.methodology_id ? byId.get(ref.methodology_id)?.name : undefined
      pushToast({
        kind: 'success',
        message:
          ref.methodology_id === null
            ? t('methodologies.picker.toast_generic')
            : name
              ? t('methodologies.picker.toast_assigned', { name })
              : t('methodologies.picker.toast_assigned_new'),
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      void queryClient.invalidateQueries({ queryKey: METHODOLOGIES_QUERY_KEY })
    },
    onError: (err) => pushToast({ kind: 'critical', message: apiErrorMessage(err) }),
  })

  const label = t('methodologies.picker.label')
  if (!editable) {
    const value = templateMethodologyLabel(template, byId, t)
    return value ? (
      <span className="text-[10px] font-mono text-ink-subtle">
        {label}: {value}
      </span>
    ) : null
  }
  const id = `template-methodology-${template.id}`
  return (
    <span className="inline-flex items-center gap-1.5">
      <label htmlFor={id} className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </label>
      <MethodologyPicker
        id={id}
        value={template.methodology_id}
        onChange={(next) => {
          if (next !== template.methodology_id) mutation.mutate(next)
        }}
        allowGeneric={template.template_type !== 'lifecycle'}
        allowCreate
        disabled={mutation.isPending}
        className="bg-canvas border border-line rounded px-1.5 py-0.5 text-[11px] text-ink focus:outline-none focus:border-brand disabled:opacity-60"
      />
    </span>
  )
}
